/**
 * ForgeCanvasModule — the live-creation client surface for The Forge (P4).
 *
 * This is the in-world control panel for System Beta's Canvas Mode plus the
 * Permission Matrix. It is a thin presentation layer over the authoritative
 * server reducers — every mutation is a single-writer reducer call, and the
 * panel simply mirrors the replicated `world_role` / `world_governance` rows:
 *
 *   • Permission Matrix — lists every (world, identity) grant for the current
 *     world with its capability set; an owner/admin can grant a role to any
 *     player present (→ `setWorldRole`) or downgrade them to a plain visitor.
 *   • World hosting / governance — the lifecycle tier, visibility, authorship,
 *     jurisdiction and live tallies, plus the quarantine / platform-lock badge
 *     (the concrete "platform jurisdiction overrides owner sovereignty" signal).
 *   • Canvas Mode — snapshot-backed edits: rez an owned asset (→ `rezInWorld`),
 *     raise terrain at the avatar's cell (→ `editTerrain`), and a global Undo
 *     (→ `undoWorldEdit`) that rolls back the last edit for everyone.
 *   • Multi-cursor presence — a Google-Docs-style overlay that projects each
 *     collaborator in the world to screen space, tagged with their role.
 *
 * Default authorship is Solo: without an explicit WorldRole granting a build
 * capability the Canvas buttons stay disabled, mirroring the server gate.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { worldToCell } from '../config.js';
import { groundYIn } from '../lib/grounding.js';

/** The capability set carried by a Permission-Matrix grant. */
interface Caps {
  enter: boolean;
  build: boolean;
  decorate: boolean;
  script: boolean;
  take: boolean;
  admin: boolean;
}

const CAP_KEYS: readonly (keyof Caps)[] = ['enter', 'build', 'decorate', 'script', 'take', 'admin'];
const ALL_CAPS: Caps = { enter: true, build: true, decorate: true, script: true, take: true, admin: true };
const NONE_CAPS: Caps = { enter: false, build: false, decorate: false, script: false, take: false, admin: false };

/** Role display ordering for the matrix (most-privileged first). */
const ROLE_ORDER: Record<string, number> = {
  owner: 0,
  admin: 1,
  build: 2,
  script: 3,
  decorate: 4,
  visitor: 5,
};

/**
 * Client mirror of the server role→caps presets (`lib/permissions.ts`). Used
 * only for display + button gating; the server is always authoritative.
 */
function defaultCapsForRole(role: string): Caps {
  switch (role) {
    case 'owner':
    case 'admin':
      return { ...ALL_CAPS };
    case 'build':
      return { ...NONE_CAPS, enter: true, build: true, decorate: true };
    case 'script':
      return { ...NONE_CAPS, enter: true, script: true };
    case 'decorate':
      return { ...NONE_CAPS, enter: true, decorate: true };
    default:
      return { ...NONE_CAPS, enter: true };
  }
}

/** Parse a `world_role.caps` JSON string, falling back to the role preset. */
function parseCaps(caps: string, role: string): Caps {
  const base = defaultCapsForRole(role);
  if (!caps) return base;
  try {
    const raw = JSON.parse(caps) as Partial<Record<keyof Caps, unknown>>;
    const out: Caps = { ...base };
    for (const k of CAP_KEYS) {
      if (typeof raw[k] === 'boolean') out[k] = raw[k] as boolean;
    }
    return out;
  } catch {
    return base;
  }
}

function shortHex(hex: string): string {
  return hex.length > 12 ? `${hex.slice(0, 6)}…${hex.slice(-4)}` : hex;
}

/** Deterministic per-identity hue so each collaborator keeps a stable colour. */
function colorFor(hex: string): string {
  let h = 0;
  for (let i = 0; i < hex.length; i++) h = (h * 31 + hex.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 70%, 62%)`;
}

export class ForgeCanvasModule implements IModule {
  readonly name = 'ForgeCanvas';

  private app!: App;
  private canvas!: HTMLCanvasElement;

  // Panel elements.
  private panel!: HTMLElement;
  private worldLabel!: HTMLElement;
  private govEl!: HTMLElement;
  private badgeEl!: HTMLElement;
  private matrixEl!: HTMLElement;
  private grantEl!: HTMLElement;
  private grantTarget!: HTMLSelectElement;
  private grantRole!: HTMLSelectElement;
  private grantBtn!: HTMLButtonElement;
  private rezBtn!: HTMLButtonElement;
  private terrainBtn!: HTMLButtonElement;
  private undoBtn!: HTMLButtonElement;
  private noteEl!: HTMLElement;

  // Cursor overlay.
  private cursorsEl!: HTMLElement;
  private cursorEls = new Map<string, HTMLElement>();

  private open = false;
  private dirty = true;
  private bound = false;
  private terrainNonce = 0;

  // Scratch vectors (reused every frame; no per-frame allocation).
  private readonly tmpWorld = new THREE.Vector3();
  private readonly tmpProj = new THREE.Vector3();
  private readonly tmpCamPos = new THREE.Vector3();
  private readonly tmpCamDir = new THREE.Vector3();
  private readonly tmpToPoint = new THREE.Vector3();

  init(app: App): void {
    this.app = app;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;

    this.panel = document.getElementById('forge')!;
    this.worldLabel = document.getElementById('forge-world')!;
    this.govEl = document.getElementById('forge-gov')!;
    this.badgeEl = document.getElementById('forge-badge')!;
    this.matrixEl = document.getElementById('forge-matrix')!;
    this.grantEl = document.getElementById('forge-grant')!;
    this.grantTarget = document.getElementById('forge-grant-target') as HTMLSelectElement;
    this.grantRole = document.getElementById('forge-grant-role') as HTMLSelectElement;
    this.grantBtn = document.getElementById('forge-grant-btn') as HTMLButtonElement;
    this.rezBtn = document.getElementById('forge-rez') as HTMLButtonElement;
    this.terrainBtn = document.getElementById('forge-terrain') as HTMLButtonElement;
    this.undoBtn = document.getElementById('forge-undo') as HTMLButtonElement;
    this.noteEl = document.getElementById('forge-note')!;
    this.cursorsEl = document.getElementById('forge-cursors')!;

    this.grantBtn.addEventListener('click', () => this.grant());
    this.rezBtn.addEventListener('click', () => this.rez());
    this.terrainBtn.addEventListener('click', () => this.raiseTerrain());
    this.undoBtn.addEventListener('click', () => this.undo());
    (document.getElementById('forge-close') as HTMLButtonElement).addEventListener('click', () => this.close());

    // G toggles; Escape closes. Ignore while typing into a field.
    window.addEventListener('keydown', (e) => {
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement;
      if (e.key === 'Escape' && this.open) {
        this.close();
      } else if ((e.key === 'g' || e.key === 'G') && !typing && atlas.hasAccount && !atlas.spaceFlight) { // in space G is the galactic chart (key-collision audit 2026-08-24)
        e.preventDefault();
        this.toggle();
      }
    });

    // Switching worlds changes the whole governance + matrix context.
    bus.on('atlas:world-entered', () => {
      this.dirty = true;
    });

    // The panel re-renders on demand; the cursor overlay updates every frame.
    app.world.addSystem(makeSystem('ForgeCanvas', Phase.UI, 25, () => this.tick()));
  }

  // ─── Lazy table binding ───────────────────────────────────────────────────────

  /** The connection appears asynchronously; bind forge-table callbacks once. */
  private ensureBound(): void {
    if (this.bound || !atlas.conn) return;
    const conn = atlas.conn;
    this.bound = true;

    const touch = (): void => {
      this.dirty = true;
    };
    conn.db.worldRole.onInsert(touch);
    conn.db.worldRole.onUpdate(touch);
    conn.db.worldRole.onDelete(touch);
    conn.db.worldGovernance.onInsert(touch);
    conn.db.worldGovernance.onUpdate(touch);
    // Without onDelete, deleteWorld cascade-deletes the governance row and the
    // Forge panel would keep showing stale quarantine / platform-lock state.
    conn.db.worldGovernance.onDelete(touch);
    this.dirty = true;
  }

  // ─── Per-frame tick ────────────────────────────────────────────────────────────

  private tick(): void {
    this.ensureBound();
    if (!this.open) return;
    if (this.dirty) {
      this.dirty = false;
      this.renderPanel();
    }
    this.renderCursors();
  }

  // ─── Open / close ────────────────────────────────────────────────────────────

  private toggle(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  private openPanel(): void {
    this.open = true;
    this.dirty = true;
    this.panel.classList.add('show');
    this.renderPanel();
  }

  private close(): void {
    this.open = false;
    this.panel.classList.remove('show');
    this.clearCursors();
  }

  // ─── Capability helpers ────────────────────────────────────────────────────────

  /** Resolve the local player's capabilities in a world (owner ⇒ all caps). */
  private localCaps(worldId: bigint): Caps {
    const conn = atlas.conn;
    const me = atlas.identityHex;
    if (!conn || !me) return { ...NONE_CAPS };
    const w = conn.db.world.worldId.find(worldId);
    if (w && w.owner.toHexString() === me) return { ...ALL_CAPS };
    for (const r of conn.db.worldRole.iter()) {
      if (r.worldId === worldId && r.identity.toHexString() === me) {
        return parseCaps(r.caps, r.role);
      }
    }
    return { ...NONE_CAPS, enter: true };
  }

  // ─── Panel render ────────────────────────────────────────────────────────────

  private renderPanel(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) {
      this.worldLabel.textContent = '';
      this.govEl.textContent = 'Not in a world yet.';
      this.badgeEl.innerHTML = '';
      this.matrixEl.innerHTML = '<div class="empty">—</div>';
      this.grantEl.classList.remove('show');
      this.setCanvasEnabled({ ...NONE_CAPS });
      return;
    }

    const world = conn.db.world.worldId.find(worldId);
    const gov = conn.db.worldGovernance.worldId.find(worldId);
    const caps = this.localCaps(worldId);

    this.worldLabel.textContent = world ? `· ${world.name}` : `· #${worldId}`;
    this.renderGovernance(world, gov);
    this.renderBadge(gov);
    this.renderMatrix(worldId, world, caps);
    this.renderGrant(caps);
    this.setCanvasEnabled(caps);
  }

  private renderGovernance(
    world: { lifecycle: string; visibility: string; sizeM: number } | null,
    gov:
      | {
          authorship: string;
          jurisdiction: string;
          template: string;
          capacity: number;
          visitorCount: number;
          verifiedReports: number;
        }
      | null,
  ): void {
    const lifecycle = world?.lifecycle ?? '—';
    const visibility = world?.visibility ?? '—';
    const rows: string[] = [
      `lifecycle   <span class="tier">${lifecycle}</span>`,
      `visibility  <b>${visibility}</b>`,
    ];
    if (gov) {
      rows.push(
        `authorship  <b>${gov.authorship}</b>`,
        `jurisdiction<b> ${gov.jurisdiction}</b>`,
        `template    <b>${gov.template}</b>`,
        `capacity    <b>${gov.capacity}</b> avatars`,
        `visitors    <b>${gov.visitorCount}</b>`,
        `reports     <b>${gov.verifiedReports}</b> verified`,
      );
    } else {
      rows.push('<span style="color:#5a6573">no governance row (Commons)</span>');
    }
    this.govEl.innerHTML = rows.join('<br>');
  }

  private renderBadge(
    gov: { quarantined: boolean; platformLocked: boolean; legacyProtected: boolean } | null,
  ): void {
    if (!gov) {
      this.badgeEl.innerHTML = '';
      return;
    }
    const flags: string[] = [];
    if (gov.platformLocked) {
      flags.push('<span class="flag locked">platform-locked</span>');
    } else if (gov.quarantined) {
      flags.push('<span class="flag quarantine">invite-only</span>');
    } else {
      flags.push('<span class="flag ok">open</span>');
    }
    if (gov.legacyProtected) flags.push('<span class="flag legacy">legacy-protected</span>');
    this.badgeEl.innerHTML = flags.join(' ');
  }

  private renderMatrix(
    worldId: bigint,
    world: { owner: Identity } | null,
    localCaps: Caps,
  ): void {
    const conn = atlas.conn!;
    const me = atlas.identityHex;
    const ownerHex = world?.owner.toHexString() ?? null;

    const rows: { hex: string; role: string; caps: Caps }[] = [];
    for (const r of conn.db.worldRole.iter()) {
      if (r.worldId !== worldId) continue;
      rows.push({ hex: r.identity.toHexString(), role: r.role, caps: parseCaps(r.caps, r.role) });
    }
    rows.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9));

    if (rows.length === 0) {
      this.matrixEl.innerHTML = '<div class="empty">No roles granted yet.</div>';
      return;
    }

    this.matrixEl.innerHTML = '';
    for (const row of rows) {
      const isYou = row.hex === me;
      const isOwner = row.hex === ownerHex || row.role === 'owner';
      const el = document.createElement('div');
      el.className = 'pm-row';

      const caps = CAP_KEYS.map(
        (k) => `<span class="cap ${row.caps[k] ? 'on' : ''}">${row.caps[k] ? k : `·${k}`}</span>`,
      ).join('');

      const canRevoke = localCaps.admin && !isOwner && !isYou;
      const revoke = canRevoke
        ? `<button class="pm-revoke" data-revoke="${row.hex}">revoke</button>`
        : '';

      el.innerHTML =
        `<div class="pm-head">` +
        `<span class="pm-who">${shortHex(row.hex)}${isYou ? ' <span class="you">(you)</span>' : ''}</span>` +
        `<span class="pm-role ${row.role}">${row.role}</span>` +
        `</div>` +
        `<div class="pm-caps">${caps}</div>` +
        (revoke ? `<div style="margin-top:6px">${revoke}</div>` : '');

      const btn = el.querySelector<HTMLButtonElement>('[data-revoke]');
      if (btn) btn.addEventListener('click', () => this.revoke(btn.dataset.revoke!));
      this.matrixEl.appendChild(el);
    }
  }

  private renderGrant(localCaps: Caps): void {
    if (!localCaps.admin) {
      this.grantEl.classList.remove('show');
      return;
    }
    this.grantEl.classList.add('show');

    // Target = any other player currently present in this world.
    const prev = this.grantTarget.value;
    this.grantTarget.innerHTML = '';
    let count = 0;
    for (const [hex] of atlas.remotes) {
      const opt = document.createElement('option');
      opt.value = hex;
      opt.textContent = shortHex(hex);
      this.grantTarget.appendChild(opt);
      count++;
    }
    if (count === 0) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = '(no other players here)';
      this.grantTarget.appendChild(opt);
      this.grantBtn.disabled = true;
    } else {
      this.grantBtn.disabled = false;
      if (prev) this.grantTarget.value = prev;
    }
  }

  private setCanvasEnabled(caps: Caps): void {
    // Mirror the server gates: rezInWorld needs build|decorate; editTerrain and
    // undoWorldEdit need build.
    this.rezBtn.disabled = !(caps.build || caps.decorate);
    this.terrainBtn.disabled = !caps.build;
    this.undoBtn.disabled = !caps.build;
  }

  // ─── Reducer actions ────────────────────────────────────────────────────────────

  private setNote(text: string, cls: '' | 'ok' | 'err' = ''): void {
    this.noteEl.textContent = text;
    this.noteEl.className = cls;
  }

  private grant(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) return;
    const hex = this.grantTarget.value;
    if (!hex) {
      this.setNote('No player selected.', 'err');
      return;
    }
    let target: Identity;
    try {
      target = Identity.fromString(hex);
    } catch {
      this.setNote('Invalid identity.', 'err');
      return;
    }
    const role = this.grantRole.value;
    this.setNote(`Granting ${role}…`);
    void conn.reducers
      .setWorldRole({ worldId, target, role, caps: '' })
      .then(() => this.setNote(`Granted ${role} to ${shortHex(hex)}.`, 'ok'))
      .catch((err: Error) => this.setNote(`grant failed: ${err.message}`, 'err'));
  }

  private revoke(hex: string): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) return;
    let target: Identity;
    try {
      target = Identity.fromString(hex);
    } catch {
      this.setNote('Invalid identity.', 'err');
      return;
    }
    // Downgrade to a plain visitor — the natural "revoke build" via the single
    // role-mutation reducer.
    this.setNote('Revoking…');
    void conn.reducers
      .setWorldRole({ worldId, target, role: 'visitor', caps: '' })
      .then(() => this.setNote(`${shortHex(hex)} is now a visitor.`, 'ok'))
      .catch((err: Error) => this.setNote(`revoke failed: ${err.message}`, 'err'));
  }

  private rez(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) return;

    // rezInWorld requires an asset the player owns — pick the first inventory item.
    const item = [...atlas.inventory.values()][0];
    if (!item) {
      this.setNote('No owned asset — create one in Passport ▸ Inventory first.', 'err');
      return;
    }
    const fx = atlas.localX - Math.sin(atlas.localYaw) * 1.5;
    const fz = atlas.localZ - Math.cos(atlas.localYaw) * 1.5;
    this.setNote('Rezzing…');
    void conn.reducers
      .rezInWorld({
        worldId,
        sourceAssetId: item.assetId,
        posX: fx,
        // Seat on the rendered ground (high-relief audit 2026-08-05).
        posY: groundYIn(conn, worldId, fx, fz) + 0.5,
        posZ: fz,
        rotX: 0,
        rotY: 0,
        rotZ: 0,
        rotW: 1,
        scale: 1,
      })
      .then(() => this.setNote(`Rezzed asset #${item.assetId} — Undo reverts it.`, 'ok'))
      .catch((err: Error) => this.setNote(`rez failed: ${err.message}`, 'err'));
  }

  private raiseTerrain(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) return;
    const cx = worldToCell(atlas.localX);
    const cz = worldToCell(atlas.localZ);
    const patchKey = `cell:${cx},${cz}`;
    const patchValue = JSON.stringify({ h: ++this.terrainNonce });
    this.setNote(`Editing terrain at cell (${cx}, ${cz})…`);
    void conn.reducers
      .editTerrain({ worldId, patchKey, patchValue })
      .then(() => this.setNote(`Raised terrain at (${cx}, ${cz}) — Undo reverts it.`, 'ok'))
      .catch((err: Error) => this.setNote(`terrain edit failed: ${err.message}`, 'err'));
  }

  private undo(): void {
    const conn = atlas.conn;
    const worldId = atlas.currentWorldId;
    if (!conn || worldId === null) return;
    this.setNote('Undoing…');
    void conn.reducers
      .undoWorldEdit({ worldId })
      .then(() => this.setNote('Undid the last edit for everyone.', 'ok'))
      .catch((err: Error) => this.setNote(`undo failed: ${err.message}`, 'err'));
  }

  // ─── Multi-cursor presence overlay ───────────────────────────────────────────────

  private renderCursors(): void {
    const worldId = atlas.currentWorldId;
    let cam: THREE.Camera | null = null;
    try {
      cam = this.app.renderer.cameras.main;
    } catch {
      cam = null;
    }
    if (!cam || worldId === null) {
      this.clearCursors();
      return;
    }

    const rect = this.canvas.getBoundingClientRect();
    cam.getWorldPosition(this.tmpCamPos);
    cam.getWorldDirection(this.tmpCamDir);

    const seen = new Set<string>();
    for (const [hex, r] of atlas.remotes) {
      seen.add(hex);
      const el = this.cursorFor(hex);

      this.tmpWorld.set(r.targetX, 1.7, r.targetZ);
      // Cull collaborators behind the camera (projection wraps for w < 0).
      this.tmpToPoint.subVectors(this.tmpWorld, this.tmpCamPos);
      if (this.tmpToPoint.dot(this.tmpCamDir) <= 0.05) {
        el.style.display = 'none';
        continue;
      }
      this.tmpProj.copy(this.tmpWorld).project(cam);
      const sx = rect.left + (this.tmpProj.x * 0.5 + 0.5) * rect.width;
      const sy = rect.top + (-this.tmpProj.y * 0.5 + 0.5) * rect.height;
      el.style.display = 'block';
      el.style.transform = `translate(${sx}px, ${sy}px)`;

      // Annotate with their live role in this world.
      const roleEl = el.querySelector<HTMLElement>('.role');
      if (roleEl) roleEl.textContent = this.roleOf(worldId, hex);
    }

    for (const [hex, el] of this.cursorEls) {
      if (!seen.has(hex)) {
        el.remove();
        this.cursorEls.delete(hex);
      }
    }
  }

  /** The role a player holds in a world, for the cursor tag ('' if none). */
  private roleOf(worldId: bigint, hex: string): string {
    const conn = atlas.conn;
    if (!conn) return '';
    const w = conn.db.world.worldId.find(worldId);
    if (w && w.owner.toHexString() === hex) return 'owner';
    for (const r of conn.db.worldRole.iter()) {
      if (r.worldId === worldId && r.identity.toHexString() === hex) return r.role;
    }
    return '';
  }

  private cursorFor(hex: string): HTMLElement {
    let el = this.cursorEls.get(hex);
    if (el) return el;
    const color = colorFor(hex);
    el = document.createElement('div');
    el.className = 'cursor';
    el.innerHTML =
      `<span class="arrow" style="color:${color}">▾</span>` +
      `<span class="tag" style="background:${color}">${shortHex(hex)}<span class="role"></span></span>`;
    this.cursorsEl.appendChild(el);
    this.cursorEls.set(hex, el);
    return el;
  }

  private clearCursors(): void {
    if (this.cursorEls.size === 0) return;
    for (const el of this.cursorEls.values()) el.remove();
    this.cursorEls.clear();
  }

  dispose(): void {
    this.clearCursors();
  }
}
